import { Html, OrbitControls, useGLTF } from "@react-three/drei";
import { Canvas, useThree } from "@react-three/fiber";
import { FC, Suspense, useEffect, useMemo, useState } from "react";
import * as THREE from "three";
import { registerCapturableCanvas } from "../../../../utils/webglCanvases";

// --- TIPI TYPESCRIPT ---
export type ConnectorGender = "male" | "female" | "both";

export type ConnectorModule = {
  width?: number;
  height?: number;
  thickness?: number;
  side?: ConnectorGender;
};

export type ConnectorLock = {
  id?: string;
  side?: ConnectorGender;
  width?: number;
  height?: number;
  thickness?: number;
  position?: [number, number, number];
  normal?: [number, number, number];
};

export type HoleFeature = {
  id?: number | string;
  original_hole_id?: number | string;
  module_index?: number;
  position?: [number, number, number];
  diameter_mm?: number;
  radius_mm?: number;
  thickness?: number;
  normal?: [number, number, number];
  type?: "standard" | "inclined_45deg" | "threaded" | "countersunk" | string;
  applicable_sides?: (ConnectorGender | string)[];
};

export type ModuleLayout = {
  count?: number;
  pitch_mm?: number;
  repeat_axis?: [number, number, number];
  placement?: "centered" | "start";
};

export type ViewPlane = "XY" | "XZ" | "YZ" | "ISO";

export type Model3DPreviewProps = {
  url?: string;
  format?: string;
  connectorModule?: ConnectorModule;
  connector?: ConnectorLock;
  holes?: HoleFeature[];
  componentRotation?: [number, number, number];
  holesRotation?: [number, number, number];
};

// --- CONTROLLORE DINAMICO CAMERA ---
const CameraController: FC<{ viewPlane: ViewPlane }> = ({ viewPlane }) => {
  const { camera } = useThree();

  useEffect(() => {
    switch (viewPlane) {
      case "XY":
        camera.position.set(0, 0, 600);
        camera.up.set(0, 1, 0);
        break;
      case "XZ":
        camera.position.set(0, 600, 0);
        camera.up.set(0, 0, -1);
        break;
      case "YZ":
        camera.position.set(600, 0, 0);
        camera.up.set(0, 1, 0);
        break;
      case "ISO":
        camera.position.set(400, 400, 600);
        camera.up.set(0, 1, 0);
        break;
    }
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
  }, [viewPlane, camera]);

  return null;
};

// --- ESPANSIONE PATTERN FORI ---
export const expandHolesPattern = (pattern: HoleFeature[], layout?: ModuleLayout): HoleFeature[] => {
  if (!pattern.length) return [];
  const count = Math.max(1, layout?.count ?? 1);
  const pitch = layout?.pitch_mm ?? 0;
  const axis = layout?.repeat_axis ?? [1, 0, 0];
  const placement = layout?.placement ?? "centered";

  const normAxis = new THREE.Vector3(...axis);
  if (normAxis.lengthSq() > 0) normAxis.normalize();
  else normAxis.set(1, 0, 0);

  const startOffset = placement === "centered" && count > 1 ? -((count - 1) * pitch) / 2 : 0;

  const expanded: HoleFeature[] = [];
  let idCounter = 1;

  for (let moduleIdx = 0; moduleIdx < count; moduleIdx++) {
    const shiftScalar = startOffset + moduleIdx * pitch;
    const shiftVec = normAxis.clone().multiplyScalar(shiftScalar);

    for (let hIdx = 0; hIdx < pattern.length; hIdx++) {
      const baseHole = pattern[hIdx];
      const basePos = baseHole?.position ?? [0, 0, 0];

      const newPos: [number, number, number] = [
        Number((basePos[0] + shiftVec.x).toFixed(3)),
        Number((basePos[1] + shiftVec.y).toFixed(3)),
        Number((basePos[2] + shiftVec.z).toFixed(3)),
      ];

      const normal = baseHole?.normal ?? [0, 0, 1];
      const isInclined = Math.abs(normal[0]) > 0.01 || Math.abs(normal[1]) > 0.01;
      const holeType = baseHole?.type ?? (isInclined ? "inclined_45deg" : "standard");

      expanded.push({
        ...baseHole,
        id: baseHole?.id ? `${baseHole?.id}_m${moduleIdx}` : idCounter++,
        original_hole_id: baseHole?.id ?? hIdx + 1,
        module_index: moduleIdx,
        type: holeType,
        position: newPos,
        normal: normal,
      });
    }
  }

  return expanded;
};

// --- CALCOLO QUATERNIONE WORLD ---
const calculateWorldQuaternion = (
  rotDeg: [number, number, number],
  baseNormal?: [number, number, number],
): THREE.Quaternion => {
  const baseQ = new THREE.Quaternion();
  if (baseNormal && baseNormal.every(Number.isFinite)) {
    const norm = new THREE.Vector3(...baseNormal).normalize();
    baseQ.setFromUnitVectors(new THREE.Vector3(0, 0, 1), norm);
  }

  const qX = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), THREE.MathUtils.degToRad(rotDeg[0]));
  const qY = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), THREE.MathUtils.degToRad(rotDeg[1]));
  const qZ = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), THREE.MathUtils.degToRad(rotDeg[2]));

  const worldQ = new THREE.Quaternion().identity().multiply(qZ).multiply(qY).multiply(qX);
  return worldQ.multiply(baseQ);
};

// --- MARKER DEI FORI ---
const HoleMarkers: FC<{ holes: HoleFeature[]; color?: string }> = ({ holes, color = "#ef4444" }) => (
  <>
    {holes.map((hole: HoleFeature, index: number) => {
      const holePos = hole.position ?? [0, 0, 0];
      if (!holePos.every(Number.isFinite)) return null;

      const position = new THREE.Vector3(...holePos);
      const radius: number = hole.radius_mm ?? (hole.diameter_mm ? hole.diameter_mm / 2 : 2.5);
      if (!Number.isFinite(radius) || radius <= 0) return null;

      const depth: number = hole.thickness && hole.thickness > 0 ? hole.thickness : radius * 3;
      const normal: THREE.Vector3 = hole.normal?.every(Number.isFinite)
        ? new THREE.Vector3(...hole.normal!).normalize()
        : new THREE.Vector3(0, 0, 1);

      const isInclined = hole.type === "inclined_45deg" || Math.abs(normal.x) > 0.01 || Math.abs(normal.y) > 0.01;
      const markerColor = isInclined ? "#f59e0b" : color;

      const cylinderRotation: THREE.Euler = new THREE.Euler().setFromQuaternion(
        new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), normal),
      );

      return (
        <mesh key={hole.id ?? index} position={position} rotation={cylinderRotation}>
          <cylinderGeometry args={[radius, radius, depth, 24]} />
          <meshBasicMaterial color={markerColor} transparent opacity={0.8} depthTest={false} />
        </mesh>
      );
    })}
  </>
);

// --- ASSI FISSI GLOBALI W ---
const GlobalAxes: FC<{ length?: number }> = ({ length = 40 }) => (
  <group position={[0, 0, 0]}>
    <axesHelper args={[length]} />
    <Html position={[length + 2, 0, 0]} center style={{ color: "#dc2626", fontWeight: "bold" }}>
      <span>X</span>
    </Html>
    <Html position={[0, length + 2, 0]} center style={{ color: "#16a34a", fontWeight: "bold" }}>
      <span>Y</span>
    </Html>
    <Html position={[0, 0, length + 2]} center style={{ color: "#2563eb", fontWeight: "bold" }}>
      <span>Z</span>
    </Html>
  </group>
);

// --- ASSI LOCALI PULITI (SENZA ELEMENTI HTML INVASIVI AL CENTRO) ---
const LocalAxes: FC<{ length?: number; suffix: string }> = ({ length = 25, suffix }) => (
  <group>
    <axesHelper args={[length]} />
    <Html position={[length + 2, 0, 0]} center style={{ color: "#ef4444", fontSize: "10px", fontWeight: "bold" }}>
      <span>X_{suffix}</span>
    </Html>
    <Html position={[0, length + 2, 0]} center style={{ color: "#22c55e", fontSize: "10px", fontWeight: "bold" }}>
      <span>Y_{suffix}</span>
    </Html>
    <Html position={[0, 0, length + 2]} center style={{ color: "#3b82f6", fontSize: "10px", fontWeight: "bold" }}>
      <span>Z_{suffix}</span>
    </Html>
  </group>
);

// --- MODELLO 3D ---
const SingleComponent: FC<{ url: string }> = ({ url }) => {
  const { scene } = useGLTF(url);
  const clonedScene: THREE.Group = useMemo(() => scene.clone(true), [scene]);
  return <primitive object={clonedScene} />;
};

// --- COMPONENTE PRINCIPALE ---
export const Model3DPreview: FC<Model3DPreviewProps> = ({
  format = "glb",
  url,
  connectorModule,
  connector,
  holes = [],
  componentRotation = [0, 0, 0],
  holesRotation = [0, 0, 0],
}) => {
  const [viewPlane, setViewPlane] = useState<ViewPlane>("XY");

  const componentWorldQuaternion = useMemo(() => {
    return calculateWorldQuaternion(componentRotation, connector?.normal);
  }, [componentRotation, connector?.normal]);

  const holesWorldQuaternion = useMemo(() => {
    return calculateWorldQuaternion(holesRotation);
  }, [holesRotation]);

  const componentPosition = connector?.position ?? [0, 0, 0];
  const side = connector?.side ?? connectorModule?.side ?? "female";

  if (format.toLowerCase() !== "glb") return <div>Supportati solo file GLB.</div>;
  if (!url) return <div>Nessun modello fornito.</div>;

  return (
    <div style={{ width: "100%", height: "400px", background: "#f8fafc", position: "relative" }}>
      {/* 🏷️ BADGE GENERE / LATO CONNETTORE (In alto a sinistra nella cornice HTML) */}
      <div
        style={{
          position: "absolute",
          top: 10,
          left: 10,
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          gap: "6px",
          background: side === "male" ? "#2563eb" : side === "female" ? "#dc2626" : "#7c3aed",
          color: "#ffffff",
          padding: "4px 10px",
          borderRadius: "6px",
          fontSize: "12px",
          fontWeight: "bold",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        }}
      >
        {side === "male" ? "♂ MASCHIO (Male)" : side === "female" ? "♀ FEMMINA (Female)" : "BOTH"}
      </div>

      {/* 🔘 BARRA PROSPETTIVA / VISTE (In alto a destra nella cornice HTML) */}
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 10,
          display: "flex",
          gap: "4px",
          background: "rgba(255, 255, 255, 0.9)",
          padding: "4px",
          borderRadius: "6px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        }}
      >
        {(["XY", "XZ", "YZ", "ISO"] as const).map((plane) => (
          <button
            key={plane}
            type="button"
            onClick={() => setViewPlane(plane)}
            style={{
              padding: "4px 10px",
              fontSize: "12px",
              fontWeight: "bold",
              border: "1px solid #cbd5e1",
              borderRadius: "4px",
              cursor: "pointer",
              background: viewPlane === plane ? "#1884a5" : "#ffffff",
              color: viewPlane === plane ? "#ffffff" : "#334155",
            }}
          >
            {plane === "XY" ? "XY (Front)" : plane === "XZ" ? "XZ (Top)" : plane === "YZ" ? "YZ (Side)" : "3D (ISO)"}
          </button>
        ))}
      </div>

      <Canvas camera={{ position: [0, 0, 600], up: [0, 1, 0], fov: 45 }} onCreated={registerCapturableCanvas}>
        <color attach="background" args={["#f8fafc"]} />
        <ambientLight intensity={1.2} />
        <directionalLight position={[0, 0, 10]} intensity={1.5} />

        <CameraController viewPlane={viewPlane} />

        <Suspense
          fallback={
            <Html center>
              <span>Caricamento 3D...</span>
            </Html>
          }
        >
          <GlobalAxes length={45} />

          {/* COMPONENTE */}
          <group quaternion={componentWorldQuaternion}>
            <group position={componentPosition}>
              <LocalAxes length={30} suffix="c" />
              <SingleComponent url={url} />
            </group>
          </group>

          {/* FORI */}
          <group quaternion={holesWorldQuaternion}>
            <LocalAxes length={30} suffix="h" />
            <HoleMarkers holes={holes} />
          </group>
        </Suspense>

        <OrbitControls makeDefault target={[0, 0, 0]} enableRotate={viewPlane === "ISO"} />
      </Canvas>
    </div>
  );
};

export default Model3DPreview;
